import { forwardRef } from "react";
import type { CheckboxGroupProps as ReactAriaCheckboxGroupProps } from "react-aria-components";
import { CheckboxGroup as ReactAriaCheckboxGroup } from "react-aria-components";
import { FieldError } from "react-hook-form";

import { cn } from "../utils";
import { NcCheckbox } from "./nc-checkbox";
import { NcFieldLayout, NcFieldLayoutProps, ncFieldLayoutVariants } from "./nc-field-layout";
import { NcLabel } from "./nc-label";

export interface NcCheckboxGroupProps extends ReactAriaCheckboxGroupProps {
  options: Array<{
    label: string;
    value: string;
  }>;
  label: string;
  hideLabel?: boolean;
  description?: string;
  error?: FieldError;
  variant?: NcFieldLayoutProps["variant"];
}

export const NcCheckboxGroup = forwardRef<HTMLDivElement, NcCheckboxGroupProps>(
  (
    {
      label,
      hideLabel,
      description,
      options,
      error,
      isRequired,
      children,
      variant,
      className,
      name,
      ...props
    },
    ref
  ) => (
    <ReactAriaCheckboxGroup
      data-nc="NcCheckboxGroup"
      className={cn(ncFieldLayoutVariants(), className)}
      ref={ref}
      name={name}
      {...props}
    >
      <NcLabel isRequired={isRequired} hideLabel={hideLabel}>
        {label}
      </NcLabel>
      <NcFieldLayout variant={variant}>
        {children ? (
          <>{children}</>
        ) : (
          options.map((option, index) => (
            <NcCheckbox
              key={option.value}
              name={name ? `${name}.${index}` : undefined}
              value={option.value}
            >
              {option.label}
            </NcCheckbox>
          ))
        )}
      </NcFieldLayout>
      {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
      <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
    </ReactAriaCheckboxGroup>
  )
);
